import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { capitalize } from '../utils/usefulFunctions';
import '../stylesheets/stylesComponents/input.scss';
import useTranslate from '../customHooks/useTranslate';

const normalizeId = value => {
    if (typeof value === 'string' && value.trim() !== '') {
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) {
            return numericValue;
        }
    }
    return value;
};

const getLabelKey = (arrayOptions, key, idValueKey) => {
    return arrayOptions.map((opt, index) => {
        return {
            ...opt,
            key: opt[idValueKey] ?? index,
            label: opt[key] || opt.Caption || opt.Name || '',
        };
    });
};

const LoadedOptionsTypeahead = props => {
    const {
        labelText = '',
        options = [],
        optionsLabelKey = 'label',
        handleChange,
        id,
        errorMessage,
        infoMessage,
        required,
        idValue,
        idValueKey = 'Id',
        value,
        multiple,
        inputProps,
        ...TypeaheadProps
    } = props;
    const translate = useTranslate();
    const label = capitalize(translate(labelText));
    const [inputValue, setInputValue] = useState('');
    const [optionsTypeahead, setOptionstypeahead] = useState(
        getLabelKey(options, optionsLabelKey, idValueKey)
    );

    useEffect(() => {
        const newOptions = getLabelKey(options, optionsLabelKey, idValueKey);
        setOptionstypeahead(newOptions);
    }, [options, optionsLabelKey, idValueKey]);

    const handleFilter = typeaheadOptions => {
        return typeaheadOptions.filter(op => {
            return op.label.toLowerCase().includes(inputValue.toLowerCase());
        });
    };

    return (
        <Stack width={'100%'}>
            <Autocomplete
                disablePortal
                id={label}
                options={optionsTypeahead}
                sx={{ width: '100%' }}
                multiple={multiple}
                renderInput={params => (
                    <TextField
                        {...params}
                        {...inputProps}
                        InputProps={{
                            ...params.InputProps,
                            ...inputProps?.InputProps,
                        }}
                        inputProps={{
                            ...params.inputProps,
                            ...inputProps?.inputProps,
                        }}
                        label={label}
                        size='small'
                        error={!!errorMessage}
                        required={required}
                    />
                )}
                onChange={(e, newValue) => {
                    // Avoid DOM setters coercing ids, arrays and null.
                    handleChange(
                        {
                            ...e,
                            target: {
                                ...e.target,
                                name: id,
                                value: multiple
                                    ? (newValue || []).map(v =>
                                          normalizeId(v[idValueKey])
                                      )
                                    : newValue
                                    ? normalizeId(newValue[idValueKey])
                                    : null,
                            },
                        },
                        newValue
                    );
                }}
                isOptionEqualToValue={(option, val) =>
                    val != null &&
                    normalizeId(option[idValueKey]) ===
                        normalizeId(val[idValueKey])
                }
                inputValue={inputValue}
                onInputChange={(e, newInputValue, reason) => {
                    if (multiple && reason === 'reset') {
                        return;
                    }
                    setInputValue(newInputValue);
                }}
                noOptionsText={errorMessage}
                filterOptions={handleFilter}
                value={
                    value
                        ? multiple
                            ? getLabelKey(value, optionsLabelKey, idValueKey)
                            : getLabelKey(
                                  [value],
                                  optionsLabelKey,
                                  idValueKey
                              )[0]
                        : multiple
                        ? optionsTypeahead.filter(opt =>
                              (idValue || []).some(
                                  idVal =>
                                      normalizeId(
                                          idVal?.[idValueKey] ?? idVal
                                      ) === normalizeId(opt[idValueKey])
                              )
                          )
                        : optionsTypeahead.find(
                              opt =>
                                  normalizeId(opt[idValueKey]) ===
                                  normalizeId(idValue)
                          ) || null
                }
                {...TypeaheadProps}
            />
            {errorMessage && (
                <p className='input__error-message'>
                    {translate(errorMessage)}
                </p>
            )}
            {infoMessage && (
                <p className='input__info-message'>
                    ⓘ {translate(infoMessage)}
                </p>
            )}
        </Stack>
    );
};

export default LoadedOptionsTypeahead;
